/**
 * FacebookPageInfo Component (OWZ-32)
 *
 * Demonstrates the `pages_manage_metadata` scope by letting the admin
 * view + edit the connected Facebook Page's basic information.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Facebook, Loader2, Pencil, RefreshCw, Save, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { facebook } from '@/services/api/admin';

const CATEGORY_OPTIONS = [
  'Martial Arts School',
  'Sports & Recreation',
  'Sports Club',
  'Education',
  'Local Business',
  'Community Organization',
];

const DAYS = [
  ['mon', 'Monday'],
  ['tue', 'Tuesday'],
  ['wed', 'Wednesday'],
  ['thu', 'Thursday'],
  ['fri', 'Friday'],
  ['sat', 'Saturday'],
  ['sun', 'Sunday'],
];

const blankHours = () => ({
  mon_1_open: '', mon_1_close: '',
  tue_1_open: '', tue_1_close: '',
  wed_1_open: '', wed_1_close: '',
  thu_1_open: '', thu_1_close: '',
  fri_1_open: '', fri_1_close: '',
  sat_1_open: '', sat_1_close: '',
  sun_1_open: '', sun_1_close: '',
});

const FacebookPageInfo = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [pageInfo, setPageInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState({
    about: '',
    description: '',
    phone: '',
    website: '',
    category: '',
    hours: blankHours(),
  });

  const fetchInfo = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await facebook.pageInfo.get();
      setPageInfo(data);
      if (data.connected !== false) {
        setForm({
          about: data.about || '',
          description: data.description || '',
          phone: data.phone || '',
          website: data.website || '',
          category: data.category || '',
          hours: { ...blankHours(), ...(data.hours || {}) },
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to load page info');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInfo();
  }, []); // intentional one-shot fetch on mount

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        about: form.about,
        description: form.description,
        phone: form.phone,
        website: form.website,
        category: form.category,
        hours: form.hours,
      };
      await facebook.pageInfo.update(payload);
      toast({ title: 'Page info updated' });
      setIsEditing(false);
      await fetchInfo();
    } catch (err) {
      toast({
        title: 'Update failed',
        description: err.message || 'Could not update page info',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Facebook Page Info</CardTitle>
          <CardDescription className="text-red-600">{error}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={fetchInfo} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (pageInfo?.connected === false) {
    return (
      <Card>
        <CardHeader className="text-center">
          <Facebook className="h-10 w-10 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
          <CardTitle>No Facebook Page connected</CardTitle>
          <CardDescription>
            Connect a Facebook Page to view and manage its profile info.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center">
          <Button onClick={() => navigate('/admin/outreach/social/platforms')}>
            Connect Facebook Page
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>{pageInfo?.page_name || 'Facebook Page'}</CardTitle>
          <CardDescription>
            Manage your Page's basic info — admins only. Updates are pushed to
            Facebook immediately.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          {!isEditing && (
            <Button variant="outline" size="sm" onClick={fetchInfo} aria-label="Refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
          {isEditing ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsEditing(false);
                  fetchInfo();
                }}
                disabled={isSaving}
              >
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={isSaving}>
                {isSaving ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                Save
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={() => setIsEditing(true)}>
              <Pencil className="h-4 w-4 mr-1" /> Edit
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="About"
            value={form.about}
            editing={isEditing}
            onChange={(v) => setForm({ ...form, about: v })}
            placeholder="Short tagline"
            display={pageInfo?.about}
          />
          <Field
            label="Phone"
            value={form.phone}
            editing={isEditing}
            onChange={(v) => setForm({ ...form, phone: v })}
            placeholder="(727) 605-3059"
            display={pageInfo?.phone}
          />
          <Field
            label="Website"
            value={form.website}
            editing={isEditing}
            onChange={(v) => setForm({ ...form, website: v })}
            placeholder="https://..."
            display={pageInfo?.website}
          />
          <div>
            <Label>Category</Label>
            {isEditing ? (
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm mt-1">{pageInfo?.category || '—'}</p>
            )}
          </div>
        </div>

        <div>
          <Label>Description</Label>
          {isEditing ? (
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={4}
              placeholder="Long description"
            />
          ) : (
            <p className="text-sm mt-1 whitespace-pre-wrap">{pageInfo?.description || '—'}</p>
          )}
        </div>

        <div>
          <Label>Business hours</Label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            Use 24-hour format (e.g., 16:00 to 20:30). Leave blank for closed.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {DAYS.map(([key, label]) => (
              <HoursRow
                key={key}
                label={label}
                dayKey={key}
                hours={form.hours}
                editing={isEditing}
                onChange={(h) => setForm({ ...form, hours: h })}
              />
            ))}
          </div>
        </div>

        {pageInfo?.fan_count != null && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {pageInfo.fan_count.toLocaleString()} followers
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const Field = ({ label, value, editing, onChange, placeholder, display }) => (
  <div>
    <Label>{label}</Label>
    {editing ? (
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    ) : (
      <p className="text-sm mt-1">{display || '—'}</p>
    )}
  </div>
);

const HoursRow = ({ label, dayKey, hours, editing, onChange }) => {
  const openKey = `${dayKey}_1_open`;
  const closeKey = `${dayKey}_1_close`;
  const open = hours[openKey] || '';
  const close = hours[closeKey] || '';

  if (!editing) {
    return (
      <div className="text-sm flex justify-between border-b border-gray-100 dark:border-gray-700 py-1">
        <span className="font-medium">{label}</span>
        <span>{open && close ? `${open} – ${close}` : 'Closed'}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Label className="w-20">{label}</Label>
      <Input
        value={open}
        onChange={(e) => onChange({ ...hours, [openKey]: e.target.value })}
        placeholder="16:00"
        className="flex-1"
      />
      <span className="text-gray-400">–</span>
      <Input
        value={close}
        onChange={(e) => onChange({ ...hours, [closeKey]: e.target.value })}
        placeholder="20:30"
        className="flex-1"
      />
    </div>
  );
};

export default FacebookPageInfo;
